<template>
  <div>
    <v-container fluid>
      <Govem_bar />
      <base-material-card
        icon="mdi-clipboard-check"
        title="รายงานผลการประเมินการปฏิบัติงานและข้อมูลการลา/มาสาย/ขาดราชการ"
        class="px-5 py-3"
      >
        <!-- ส่วนการตั้งค่า -->
        <v-row class="mb-4">
          <v-col cols="12">
            <v-alert
              type="info"
              prominent
              outlined
              border="left"
              colored-border
              elevation="2"
              icon="mdi-information"
            >
              <div class="text-subtitle-1 font-weight-medium">
                รายงานข้อมูลการประเมินการปฏิบัติงาน และรายงานข้อมูลการลา การมาสาย และ การขาดราชการของพนักงานราชการ
              </div>
              <div class="text-body-2">
                ปีงบประมาณ พ.ศ. {{ selectedYear }} - {{ collegeData.user_firstname || 'ไม่ระบุสถาบัน' }}
              </div>
            </v-alert>
          </v-col>
          
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedYear"
              :items="years"
              label="ปีงบประมาณ"
              outlined
              dense
              @change="loadAllData"
            ></v-select>
          </v-col>
          
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedTab"
              :items="tabOptions"
              label="ประเภทรายงาน"
              outlined
              dense
              @change="switchTab"
            ></v-select>
          </v-col>
          
          <v-col cols="12" md="6" class="text-right">
            <v-btn
              color="primary"
              @click="loadAllData"
              :loading="loading"
              class="mr-2"
            >
              <v-icon left>mdi-refresh</v-icon>
              โหลดข้อมูลใหม่
            </v-btn>
            
            <v-btn
              color="success"
              @click="generateReport"
              :disabled="!canGenerateReport"
              class="mr-2"
            >
              <v-icon left>mdi-chart-line</v-icon>
              สร้างรายงานสรุป
            </v-btn>
            
            <v-btn
              color="info"
              @click="exportToExcel"
              :disabled="currentTabData.length === 0"
            >
              <v-icon left>mdi-file-excel</v-icon>
              ส่งออก Excel
            </v-btn>
          </v-col>
        </v-row>

        <!-- สรุปข้อมูลรวม -->
        <v-row class="mb-4" v-if="reportSummary">
          <v-col cols="12">
            <v-card class="pa-4" outlined>
              <v-row>
                <v-col cols="12" md="2">
                  <v-card-text class="text-center">
                    <h3 class="blue--text">{{ reportSummary.total_employees || 0 }}</h3>
                    <p>จำนวนพนักงานทั้งหมด</p>
                  </v-card-text>
                </v-col>
                <v-col cols="12" md="2">
                  <v-card-text class="text-center">
                    <h3 class="green--text">{{ reportSummary.submitted_assessments || 0 }}</h3>
                    <p>ประเมินแล้ว</p>
                  </v-card-text>
                </v-col>
                <v-col cols="12" md="2">
                  <v-card-text class="text-center">
                    <h3 class="purple--text">{{ formatNumber(reportSummary.avg_performance_score) }}</h3>
                    <p>คะแนนเฉลี่ย</p>
                  </v-card-text>
                </v-col>
                <v-col cols="12" md="2">
                  <v-card-text class="text-center">
                    <h3 class="orange--text">{{ reportSummary.total_leave_days || 0 }}</h3>
                    <p>วันลารวม</p>
                  </v-card-text>
                </v-col>
                <v-col cols="12" md="2">
                  <v-card-text class="text-center">
                    <h3 class="red--text">{{ reportSummary.total_late_days || 0 }}</h3>
                    <p>วันมาสายรวม</p>
                  </v-card-text>
                </v-col>
                <v-col cols="12" md="2">
                  <v-card-text class="text-center">
                    <h3 class="deep-orange--text">{{ reportSummary.total_discipline_cases || 0 }}</h3>
                    <p>คดีวินัยรวม</p>
                  </v-card-text>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!-- Tabs สำหรับแสดงข้อมูลแต่ละประเภท -->
        <v-tabs v-model="activeTab" background-color="transparent" color="primary">
          <v-tab key="assessment">การประเมินการปฏิบัติงาน</v-tab>
          <v-tab key="attendance">การลา/มาสาย/ขาดราชการ</v-tab>
          <v-tab key="discipline">การลงโทษทางวินัย</v-tab>
        </v-tabs>

        <v-tabs-items v-model="activeTab">
          <!-- Tab การประเมินการปฏิบัติงาน -->
          <v-tab-item key="assessment">
            <v-card flat>
              <v-card-title>
                <span>การประเมินการปฏิบัติงาน ปีงบประมาณ {{ selectedYear }}</span>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  small
                  @click="openAssessmentDialog()"
                >
                  <v-icon left>mdi-plus</v-icon>
                  เพิ่มการประเมิน
                </v-btn>
              </v-card-title>
              
              <v-data-table
                :headers="assessmentHeaders"
                :items="assessmentData"
                :loading="loading"
                class="elevation-1"
                :items-per-page="15"
              >
                <template v-slot:item.performance_score="{ item }">
                  <v-chip
                    small
                    :color="getScoreColor(item.performance_score)"
                    text-color="white"
                  >
                    {{ formatNumber(item.performance_score) }}
                  </v-chip>
                </template>

                <template v-slot:item.performance_grade="{ item }">
                  <v-chip
                    small
                    :color="getGradeColor(item.performance_grade)"
                    text-color="white"
                  >
                    {{ item.performance_grade || 'ไม่ระบุ' }}
                  </v-chip>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="info"
                        v-bind="attrs"
                        v-on="on"
                        @click="openAssessmentDialog(item)"
                      >
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>แก้ไข</span>
                  </v-tooltip>
                  
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="error"
                        v-bind="attrs"
                        v-on="on"
                        @click="deleteAssessment(item)"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>ลบ</span>
                  </v-tooltip>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>

          <!-- Tab การลา/มาสาย/ขาดราชการ -->
          <v-tab-item key="attendance">
            <v-card flat>
              <v-card-title>
                <span>ข้อมูลการลา/มาสาย/ขาดราชการ ปีงบประมาณ {{ selectedYear }}</span>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  small
                  @click="openAttendanceDialog()"
                >
                  <v-icon left>mdi-plus</v-icon>
                  เพิ่มข้อมูล
                </v-btn>
              </v-card-title>
              
              <v-data-table
                :headers="attendanceHeaders"
                :items="attendanceData"
                :loading="loading"
                class="elevation-1"
                :items-per-page="15"
              >
                <template v-slot:item.leave_days="{ item }">
                  <span class="orange--text font-weight-bold">{{ item.leave_days }}</span>
                </template>

                <template v-slot:item.late_days="{ item }">
                  <span class="red--text font-weight-bold">{{ item.late_days }}</span>
                </template>

                <template v-slot:item.absent_days="{ item }">
                  <span class="deep-orange--text font-weight-bold">{{ item.absent_days }}</span>
                </template>

                <template v-slot:item.actual_work_days="{ item }">
                  <span class="green--text font-weight-bold">{{ item.actual_work_days }}</span>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="info"
                        v-bind="attrs"
                        v-on="on"
                        @click="openAttendanceDialog(item)"
                      >
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>แก้ไข</span>
                  </v-tooltip>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>

          <!-- Tab การลงโทษทางวินัย -->
          <v-tab-item key="discipline">
            <v-card flat>
              <v-card-title>
                <span>การถูกสั่งลงโทษทางวินัย/ถูกศาลพิพากษาคดีอาญา ปีงบประมาณ {{ selectedYear }}</span>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary"
                  small
                  @click="openDisciplineDialog()"
                >
                  <v-icon left>mdi-plus</v-icon>
                  เพิ่มข้อมูล
                </v-btn>
              </v-card-title>
              
              <v-data-table
                :headers="disciplineHeaders"
                :items="disciplineData"
                :loading="loading"
                class="elevation-1"
                :items-per-page="15"
              >
                <template v-slot:item.discipline_type="{ item }">
                  <v-chip
                    small
                    :color="getDisciplineTypeColor(item.discipline_type)"
                    text-color="white"
                  >
                    {{ item.discipline_type }}
                  </v-chip>
                </template>

                <template v-slot:item.court_case="{ item }">
                  <v-chip
                    small
                    :color="item.court_case ? 'red' : 'green'"
                    text-color="white"
                  >
                    {{ item.court_case ? 'มีคดีอาญา' : 'ไม่มีคดีอาญา' }}
                  </v-chip>
                </template>

                <template v-slot:item.discipline_date="{ item }">
                  {{ formatDate(item.discipline_date) }}
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="info"
                        v-bind="attrs"
                        v-on="on"
                        @click="viewDisciplineDetail(item)"
                      >
                        <v-icon>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>ดูรายละเอียด</span>
                  </v-tooltip>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </base-material-card>

      <!-- Dialog การประเมินการปฏิบัติงาน -->
      <v-dialog v-model="assessmentDialog" max-width="800px">
        <v-card>
          <v-card-title>
            <span class="headline">{{ editingAssessment.id ? 'แก้ไข' : 'เพิ่ม' }}การประเมินการปฏิบัติงาน</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="editingAssessment.id_card"
                    :items="personnelList"
                    item-text="full_name"
                    item-value="id_card"
                    label="เลือกพนักงาน"
                    outlined
                    :disabled="!!editingAssessment.id"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="editingAssessment.performance_score"
                    label="คะแนนการปฏิบัติงาน"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="editingAssessment.performance_grade"
                    :items="gradeOptions"
                    label="เกรดการประเมิน"
                    outlined
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="editingAssessment.performance_note"
                    label="หมายเหตุการประเมิน"
                    outlined
                    rows="3"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="assessmentDialog = false">
              ยกเลิก
            </v-btn>
            <v-btn color="blue darken-1" text @click="saveAssessment">
              บันทึก
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog การลา/มาสาย/ขาดราชการ -->
      <v-dialog v-model="attendanceDialog" max-width="900px">
        <v-card>
          <v-card-title>
            <span class="headline">{{ editingAttendance.id ? 'แก้ไข' : 'เพิ่ม' }}ข้อมูลการลา/มาสาย/ขาดราชการ</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="editingAttendance.id_card"
                    :items="personnelList"
                    item-text="full_name"
                    item-value="id_card"
                    label="เลือกพนักงาน"
                    outlined
                    :disabled="!!editingAttendance.id"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="editingAttendance.total_work_days"
                    label="จำนวนวันทำงานทั้งหมด"
                    type="number"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.number="editingAttendance.leave_days"
                    label="จำนวนวันลา"
                    type="number"
                    min="0"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.number="editingAttendance.late_days"
                    label="จำนวนวันมาสาย"
                    type="number"
                    min="0"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.number="editingAttendance.absent_days"
                    label="จำนวนวันขาดราชการ"
                    type="number"
                    min="0"
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-textarea
                    v-model="editingAttendance.leave_detail"
                    label="รายละเอียดการลา"
                    outlined
                    rows="2"
                  ></v-textarea>
                </v-col>
                <v-col cols="12" md="4">
                  <v-textarea
                    v-model="editingAttendance.late_detail"
                    label="รายละเอียดการมาสาย"
                    outlined
                    rows="2"
                  ></v-textarea>
                </v-col>
                <v-col cols="12" md="4">
                  <v-textarea
                    v-model="editingAttendance.absent_detail"
                    label="รายละเอียดการขาดราชการ"
                    outlined
                    rows="2"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="attendanceDialog = false">
              ยกเลิก
            </v-btn>
            <v-btn color="blue darken-1" text @click="saveAttendance">
              บันทึก
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog การลงโทษทางวินัย -->
      <v-dialog v-model="disciplineDialog" max-width="1000px">
        <v-card>
          <v-card-title>
            <span class="headline">{{ editingDiscipline.id ? 'แก้ไข' : 'เพิ่ม' }}การลงโทษทางวินัย/คดีอาญา</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="editingDiscipline.id_card"
                    :items="personnelList"
                    item-text="full_name"
                    item-value="id_card"
                    label="เลือกพนักงาน"
                    outlined
                    :disabled="!!editingDiscipline.id"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="editingDiscipline.discipline_type"
                    :items="disciplineTypes"
                    label="ประเภทการลงโทษ"
                    outlined
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-date-picker
                    v-model="editingDiscipline.discipline_date"
                    label="วันที่ถูกลงโทษ"
                  ></v-date-picker>
                </v-col>
                <v-col cols="12" md="6">
                  <v-switch
                    v-model="editingDiscipline.court_case"
                    :true-value="1"
                    :false-value="0"
                    label="มีคดีอาญา"
                  ></v-switch>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="editingDiscipline.discipline_reason"
                    label="เหตุผลการลงโทษ"
                    outlined
                    rows="3"
                  ></v-textarea>
                </v-col>
                <v-col cols="12" md="6" v-if="editingDiscipline.court_case">
                  <v-textarea
                    v-model="editingDiscipline.court_detail"
                    label="รายละเอียดคดีอาญา"
                    outlined
                    rows="3"
                  ></v-textarea>
                </v-col>
                <v-col cols="12" md="6">
                  <v-file-input
                    v-model="disciplineFile"
                    label="แนบเอกสารคำสั่งลงโทษ"
                    outlined
                    accept="image/*,.pdf"
                  ></v-file-input>
                </v-col>
                <v-col cols="12" md="6" v-if="editingDiscipline.court_case">
                  <v-file-input
                    v-model="courtFile"
                    label="แนบเอกสารคำพิพากษา"
                    outlined
                    accept="image/*,.pdf"
                  ></v-file-input>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="disciplineDialog = false">
              ยกเลิก
            </v-btn>
            <v-btn color="blue darken-1" text @click="saveDiscipline">
              บันทึก
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar แสดงผลลัพธ์ -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        top
      >
        <v-icon left>{{ snackbar.icon }}</v-icon>
        {{ snackbar.text }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">
            ปิด
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import Govem_bar from "../../../components/college/govem_bar.vue";
export default {
  name: "AssessmentGovem",
  components: {
    Govem_bar
  },
  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      selectedYear: new Date().getFullYear() + 543,
      activeTab: 0,
      selectedTab: 'assessment',

      // ข้อมูลผู้ใช้และวิทยาลัย
      user: {},
      collegeData: {},
      personnelList: [],

      // ข้อมูลแต่ละ tab
      assessmentData: [],
      attendanceData: [],
      disciplineData: [],
      reportSummary: null,

      // Dialog states
      assessmentDialog: false,
      attendanceDialog: false,
      disciplineDialog: false,

      // Editing objects
      editingAssessment: {
        id: null,
        id_card: '',
        performance_score: 0,
        performance_grade: '',
        performance_note: ''
      },
      editingAttendance: {
        id: null,
        id_card: '',
        leave_days: 0,
        late_days: 0,
        absent_days: 0,
        total_work_days: 250,
        leave_detail: '',
        late_detail: '',
        absent_detail: ''
      },
      editingDiscipline: {
        id: null,
        id_card: '',
        discipline_type: '',
        discipline_reason: '',
        discipline_date: new Date().toISOString().substr(0, 10),
        court_case: 0,
        court_detail: ''
      },

      // Files
      disciplineFile: null,
      courtFile: null,

      // ตัวเลือกปี
      years: [],

      // Tab options
      tabOptions: [
        { text: 'การประเมินการปฏิบัติงาน', value: 'assessment' },
        { text: 'การลา/มาสาย/ขาดราชการ', value: 'attendance' },
        { text: 'การลงโทษทางวินัย', value: 'discipline' }
      ],

      // Grade options
      gradeOptions: [
        'ดีเด่น',
        'ดีมาก',
        'ดี',
        'พอใช้',
        'ต้องปรับปรุง'
      ],

      // Discipline types
      disciplineTypes: [
        'ตักเตือน',
        'ภาคทัณฑ์',
        'ตัดเงินเดือน',
        'ลดเงินเดือน',
        'ปลดออก',
        'ไล่ออก',
        'อื่นๆ'
      ],

      // Headers สำหรับตาราง
      assessmentHeaders: [
        { text: "ลำดับ", value: "id", width: "80px" },
        { text: "ชื่อ-สกุล", value: "full_name", width: "200px" },
        { text: "ตำแหน่ง", value: "position_name", width: "150px" },
        { text: "คะแนน", value: "performance_score", width: "100px", align: "center" },
        { text: "เกรด", value: "performance_grade", width: "120px", align: "center" },
        { text: "หมายเหตุ", value: "performance_note", width: "200px" },
        { text: "ดำเนินการ", value: "actions", width: "120px", align: "center", sortable: false }
      ],

      attendanceHeaders: [
        { text: "ลำดับ", value: "id", width: "80px" },
        { text: "ชื่อ-สกุล", value: "full_name", width: "200px" },
        { text: "ตำแหน่ง", value: "position_name", width: "150px" },
        { text: "วันทำงานทั้งหมด", value: "total_work_days", width: "120px", align: "center" },
        { text: "วันลา", value: "leave_days", width: "80px", align: "center" },
        { text: "วันมาสาย", value: "late_days", width: "80px", align: "center" },
        { text: "วันขาด", value: "absent_days", width: "80px", align: "center" },
        { text: "วันทำงานจริง", value: "actual_work_days", width: "120px", align: "center" },
        { text: "ดำเนินการ", value: "actions", width: "120px", align: "center", sortable: false }
      ],

      disciplineHeaders: [
        { text: "ลำดับ", value: "id", width: "80px" },
        { text: "ชื่อ-สกุล", value: "full_name", width: "200px" },
        { text: "ประเภทการลงโทษ", value: "discipline_type", width: "150px" },
        { text: "วันที่ถูกลงโทษ", value: "discipline_date", width: "120px" },
        { text: "คดีอาญา", value: "court_case", width: "100px", align: "center" },
        { text: "เหตุผล", value: "discipline_reason", width: "200px" },
        { text: "ดำเนินการ", value: "actions", width: "120px", align: "center", sortable: false }
      ],

      // Snackbar
      snackbar: {
        show: false,
        color: "success",
        timeout: 3000,
        icon: "mdi-check",
        text: ""
      }
    };
  },

  computed: {
    canGenerateReport() {
      return this.assessmentData.length > 0 || this.attendanceData.length > 0 || this.disciplineData.length > 0;
    },
    
    currentTabData() {
      switch (this.activeTab) {
        case 0: return this.assessmentData;
        case 1: return this.attendanceData;
        case 2: return this.disciplineData;
        default: return [];
      }
    }
  },

  async mounted() {
    await this.initializeComponent();
  },

  methods: {
    async initializeComponent() {
      this.loading = true;
      try {
        // โหลดข้อมูล user session
        let userSession = JSON.parse(sessionStorage.getItem("user")) || {};

        let resultUser = await this.$http.post("user.php", {
          ApiKey: this.ApiKey,
          user_ID: userSession.user_ID
        });
        this.user = resultUser.data;
        this.collegeData = resultUser.data;

        // สร้างรายการปี
        this.generateYears();

        // โหลดรายชื่อพนักงาน
        await this.loadPersonnelList();

        // โหลดข้อมูลทั้งหมด
        await this.loadAllData();
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูล", "error", "mdi-alert");
      } finally {
        this.loading = false;
      }
    },

    generateYears() {
      const currentYear = new Date().getFullYear() + 543;
      this.years = [];
      for (let i = 0; i < 5; i++) {
        this.years.push(currentYear - i);
      }
    },

    async loadPersonnelList() {
      try {
        let result = await this.$http.post("personnel_temporary_govem.php", {
          ApiKey: this.ApiKey,
          user_code: this.user.user_code,
          user_status: "gov_em"
        });

        this.personnelList = result.data.map(person => ({
          ...person,
          full_name: `${person.frist_name} ${person.last_name}`,
        }));
      } catch (error) {
        this.showSnackbar("ไม่สามารถโหลดรายชื่อพนักงานได้", "error", "mdi-alert");
      }
    },

    async loadAllData() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadAssessmentData(),
          this.loadAttendanceData(),
          this.loadDisciplineData(),
          this.loadReportSummary()
        ]);
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูล", "error", "mdi-alert");
      } finally {
        this.loading = false;
      }
    },

    async loadAssessmentData() {
      try {
        let result = await this.$http.post("gov_em_assessment.php", {
          ApiKey: this.ApiKey,
          action: "get",
          college_code: this.user.user_code,
          assessment_year: this.selectedYear
        });
        this.assessmentData = result.data;
      } catch (error) {
        this.assessmentData = [];
      }
    },

    async loadAttendanceData() {
      try {
        let result = await this.$http.post("gov_em_attendance.php", {
          ApiKey: this.ApiKey,
          action: "get",
          college_code: this.user.user_code,
          attendance_year: this.selectedYear
        });
        this.attendanceData = result.data;
      } catch (error) {
        this.attendanceData = [];
      }
    },

    async loadDisciplineData() {
      try {
        let result = await this.$http.post("gov_em_discipline.php", {
          ApiKey: this.ApiKey,
          action: "get",
          college_code: this.user.user_code,
          discipline_year: this.selectedYear
        });
        this.disciplineData = result.data;
      } catch (error) {
        this.disciplineData = [];
      }
    },

    async loadReportSummary() {
      try {
        let result = await this.$http.post("gov_em_assessment_report.php", {
          ApiKey: this.ApiKey,
          action: "get_report",
          college_code: this.user.user_code,
          report_year: this.selectedYear
        });
        this.reportSummary = result.data;
      } catch (error) {
        this.reportSummary = null;
      }
    },

    switchTab() {
      // เปลี่ยน tab ตาม selectedTab
      const tabIndex = this.tabOptions.findIndex(tab => tab.value === this.selectedTab);
      if (tabIndex >= 0) {
        this.activeTab = tabIndex;
      }
    },

    // === การประเมินการปฏิบัติงาน ===
    openAssessmentDialog(item = null) {
      if (item) {
        this.editingAssessment = { ...item };
      } else {
        this.editingAssessment = {
          id: null,
          id_card: '',
          performance_score: 0,
          performance_grade: '',
          performance_note: ''
        };
      }
      this.assessmentDialog = true;
    },

    async saveAssessment() {
      try {
        const action = this.editingAssessment.id ? 'update' : 'insert';
        const payload = {
          ApiKey: this.ApiKey,
          action: action,
          ...this.editingAssessment,
          assessment_year: this.selectedYear,
          college_code: this.user.user_code
        };

        let result = await this.$http.post("gov_em_assessment.php", payload);
        console.log(result.data)
        if (result.data.status) {
          this.showSnackbar("บันทึกการประเมินเรียบร้อย", "success", "mdi-check");
          this.assessmentDialog = false;
          await this.loadAssessmentData();
        } else {
          this.showSnackbar("เกิดข้อผิดพลาดในการบันทึก", "error", "mdi-alert");
        }
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการบันทึก", "error", "mdi-alert");
      }
    },

    async deleteAssessment(item) {
      if (confirm(`ต้องการลบการประเมินของ ${item.full_name} หรือไม่?`)) {
        try {
          let result = await this.$http.post("gov_em_assessment.php", {
            ApiKey: this.ApiKey,
            action: "delete",
            id: item.id
          });
          
          if (result.data.status) {
            this.showSnackbar("ลบการประเมินเรียบร้อย", "success", "mdi-check");
            await this.loadAssessmentData();
          } else {
            this.showSnackbar("เกิดข้อผิดพลาดในการลบ", "error", "mdi-alert");
          }
        } catch (error) {
          this.showSnackbar("เกิดข้อผิดพลาดในการลบ", "error", "mdi-alert");
        }
      }
    },

    // === การลา/มาสาย/ขาดราชการ ===
    openAttendanceDialog(item = null) {
      if (item) {
        this.editingAttendance = { ...item };
      } else {
        this.editingAttendance = {
          id: null,
          id_card: '',
          leave_days: 0,
          late_days: 0,
          absent_days: 0,
          total_work_days: 250,
          leave_detail: '',
          late_detail: '',
          absent_detail: ''
        };
      }
      this.attendanceDialog = true;
    },

    async saveAttendance() {
      try {
        const action = this.editingAttendance.id ? 'update' : 'insert';
        const payload = {
          ApiKey: this.ApiKey,
          action: action,
          ...this.editingAttendance,
          attendance_year: this.selectedYear,
          college_code: this.user.user_code
        };

        let result = await this.$http.post("gov_em_attendance.php", payload);
        
        if (result.data.status) {
          this.showSnackbar("บันทึกข้อมูลการลาเรียบร้อย", "success", "mdi-check");
          this.attendanceDialog = false;
          await this.loadAttendanceData();
        } else {
          this.showSnackbar("เกิดข้อผิดพลาดในการบันทึก", "error", "mdi-alert");
        }
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการบันทึก", "error", "mdi-alert");
      }
    },

    // === การลงโทษทางวินัย ===
    openDisciplineDialog(item = null) {
      if (item) {
        this.editingDiscipline = { ...item };
      } else {
        this.editingDiscipline = {
          id: null,
          id_card: '',
          discipline_type: '',
          discipline_reason: '',
          discipline_date: new Date().toISOString().substr(0, 10),
          court_case: 0,
          court_detail: ''
        };
      }
      this.disciplineDialog = true;
    },

    async saveDiscipline() {
      try {
        const action = this.editingDiscipline.id ? 'update' : 'insert';
        const payload = {
          ApiKey: this.ApiKey,
          action: action,
          ...this.editingDiscipline,
          discipline_year: this.selectedYear,
          college_code: this.user.user_code
        };

        let result = await this.$http.post("gov_em_discipline.php", payload);
        
        if (result.data.status) {
          this.showSnackbar("บันทึกข้อมูลการลงโทษเรียบร้อย", "success", "mdi-check");
          this.disciplineDialog = false;
          await this.loadDisciplineData();
        } else {
          this.showSnackbar("เกิดข้อผิดพลาดในการบันทึก", "error", "mdi-alert");
        }
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการบันทึก", "error", "mdi-alert");
      }
    },

    viewDisciplineDetail(item) {
      // แสดงรายละเอียดในแบบ dialog หรือ modal
      alert(`รายละเอียด: ${item.discipline_reason}\n${item.court_case ? 'มีคดีอาญา: ' + item.court_detail : 'ไม่มีคดีอาญา'}`);
    },

    // === การสร้างรายงาน ===
    async generateReport() {
      this.loading = true;
      try {
        let result = await this.$http.post("gov_em_assessment_report.php", {
          ApiKey: this.ApiKey,
          action: "generate_report",
          college_code: this.user.user_code,
          report_year: this.selectedYear
        });
        
        if (result.data.status) {
          this.reportSummary = result.data.summary;
          this.showSnackbar("สร้างรายงานสรุปเรียบร้อย", "success", "mdi-check");
        } else {
          this.showSnackbar("เกิดข้อผิดพลาดในการสร้างรายงาน", "error", "mdi-alert");
        }
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการสร้างรายงาน", "error", "mdi-alert");
      } finally {
        this.loading = false;
      }
    },

    async exportToExcel() {
      try {
        const exportData = {
          assessment: this.assessmentData,
          attendance: this.attendanceData,
          discipline: this.disciplineData,
          summary: this.reportSummary
        };

        let result = await this.$http.post("gov_em_assessment_export.php", {
          ApiKey: this.ApiKey,
          college_code: this.user.user_code,
          report_year: this.selectedYear,
          export_data: exportData
        });

        if (result.data.file_url) {
          window.open(result.data.file_url, "_blank");
          this.showSnackbar("ส่งออกรายงานเรียบร้อย", "success", "mdi-download");
        }
      } catch (error) {
        this.showSnackbar("เกิดข้อผิดพลาดในการส่งออกรายงาน", "error", "mdi-alert");
      }
    },

    // === Helper Functions ===
    getScoreColor(score) {
      if (score >= 90) return "green";
      if (score >= 80) return "blue";
      if (score >= 70) return "orange";
      if (score >= 60) return "yellow darken-2";
      return "red";
    },

    getGradeColor(grade) {
      const gradeColors = {
        'ดีเด่น': 'green',
        'ดีมาก': 'blue',
        'ดี': 'teal',
        'พอใช้': 'orange',
        'ต้องปรับปรุง': 'red'
      };
      return gradeColors[grade] || 'grey';
    },

    getDisciplineTypeColor(type) {
      const typeColors = {
        'ตักเตือน': 'yellow darken-2',
        'ภาคทัณฑ์': 'orange',
        'ตัดเงินเดือน': 'deep-orange',
        'ลดเงินเดือน': 'red',
        'ปลดออก': 'red darken-2',
        'ไล่ออก': 'red darken-4'
      };
      return typeColors[type] || 'grey';
    },

    formatNumber(value) {
      if (isNaN(value) || value === null || value === undefined) return "0.00";
      return Number(value).toFixed(2);
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('th-TH');
    },

    showSnackbar(text, color = "success", icon = "mdi-check") {
      this.snackbar = {
        show: true,
        text,
        color,
        icon,
        timeout: 3000
      };
    }
  }
};
</script>

<style scoped>
.v-data-table >>> .v-data-table__wrapper {
  overflow-x: auto;
}

.v-card-text h3 {
  font-size: 1.5rem;
  font-weight: bold;
}

.v-card-text p {
  margin-bottom: 0;
  font-size: 0.875rem;
}

.headline {
  font-weight: 500;
}

.v-text-field >>> .v-input__control {
  min-height: 40px;
}

.elevation-1 {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* สีสำหรับแยกแยะข้อมูลในตาราง */
.success--text { color: #4caf50 !important; }
.info--text { color: #2196f3 !important; }
.warning--text { color: #ff9800 !important; }
.error--text { color: #f44336 !important; }
.primary--text { color: #1976d2 !important; }
.orange--text { color: #ff5722 !important; }
.green--text { color: #388e3c !important; }
.red--text { color: #d32f2f !important; }
.purple--text { color: #7b1fa2 !important; }
.blue--text { color: #1565c0 !important; }
.deep-orange--text { color: #ff5722 !important; }

/* สำหรับตารางที่มีความกว้างมาก */
.v-data-table >>> .v-data-table__wrapper {
  max-height: 600px;
  overflow-y: auto;
}

/* การ์ดสรุป */
.v-card.outlined {
  border: 1px solid #e0e0e0;
}

/* ปรับแต่ง tabs */
.v-tabs >>> .v-tab {
  text-transform: none;
  font-weight: 500;
}

/* ปรับแต่ง dialog */
.v-dialog >>> .v-card__title {
  background-color: #f5f5f5;
}

/* ปรับแต่ง date picker */
.v-date-picker >>> .v-date-picker__title {
  background-color: #1976d2;
}
</style>